/**
 * Helpers for browser → data-hub-action requests (query params, org id).
 */

import { allowed_generic_h_hash_key_names } from './static_vars';

const uuid_v4_pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Returns a trimmed organization UUID string or null if missing / invalid.
 * Matches data-hub-action expectations for explicit organization_id values.
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalize_data_hub_organization_id_string(value) {
	if (value === null || value === undefined) {
		return null;
	}
	if (typeof value !== 'string') {
		return null;
	}
	const trimmed_organization_id = value.trim();
	if (!trimmed_organization_id) {
		return null;
	}
	if (!uuid_v4_pattern.test(trimmed_organization_id)) {
		return null;
	}
	return trimmed_organization_id;
}

/**
 * True when the candidate is a non-null plain object (not array) carrying at minimum
 * a UUID-valid organization_id. organization_id is the only field the data_hub_class
 * absolutely needs to address the right org; without it the metadata cannot be used
 * for forms. All other fields (theme_json, iterable_*, data_hub_action_domain, ...)
 * are optional for this gate.
 *
 * For the stricter "complete enough to skip the GET fetch" check (which also requires
 * a valid forms domain URL), see organization_metadata_is_complete_enough_to_skip_fetch
 * in src/forms/data_hub_metadata.js.
 *
 * @param {unknown} candidate_organization_metadata
 * @returns {boolean}
 */
export function is_valid_organization_metadata(candidate_organization_metadata) {
	if (!candidate_organization_metadata || typeof candidate_organization_metadata !== 'object' || Array.isArray(candidate_organization_metadata)) {
		return false;
	}
	return Boolean(normalize_data_hub_organization_id_string(candidate_organization_metadata.organization_id));
}

/**
 * Appends or replaces organization_id on a fully qualified URL (GET requests to action).
 * @param {string} url_string
 * @param {string|null|undefined} organization_id
 * @returns {string}
 */
export function append_organization_id_query_param_to_url(url_string, organization_id) {
	const normalized_organization_id = normalize_data_hub_organization_id_string(organization_id);
	if (!url_string || !normalized_organization_id) {
		return url_string;
	}
	try {
		const parsed_url = new URL(url_string);
		parsed_url.searchParams.set('organization_id', normalized_organization_id);
		return parsed_url.toString();
	} catch {
		return url_string;
	}
}

/**
 * Maps an HMAC query/body param name to the plain person field name (same rules as data-hub-action process_hmac.js).
 *
 * Generic h_* keys are checked FIRST so that 'h_iterable_user_id' (generic: h_ + iterable_user_id)
 * is not confused with the legacy h_iterable_ prefix (which would yield 'user_id' instead of 'iterable_user_id').
 *
 * @param {string} hmac_key_name
 * @returns {string}
 */
export function hmac_param_name_to_plain_field_name(hmac_key_name) {
	if (typeof hmac_key_name !== 'string') {
		return hmac_key_name;
	}
	// Generic list takes priority: strip only h_ so 'h_iterable_user_id' → 'iterable_user_id'
	if (allowed_generic_h_hash_key_names.includes(hmac_key_name) && hmac_key_name.startsWith('h_')) {
		return hmac_key_name.replace(/^h_/, '');
	}
	if (hmac_key_name.startsWith('h_iterable_')) {
		return hmac_key_name.replace('h_iterable_', '');
	}
	if (hmac_key_name.startsWith('h_')) {
		return hmac_key_name.replace('h_', '');
	}
	return hmac_key_name;
}
